// DialerPanel.js — v3 (CallHippo via your backend proxy)
// -------------------------------------------------------------------
// ✅ Floating bubble + drawer unchanged
// ✅ Calls YOUR backend: /dialer/call, /dialer/hangup, /dialer/status
// ✅ Stores callId + polls status while active
// ✅ Keeps screen-pop via onScreenPop(mobile)
// ✅ No CallHippo token in frontend (server-side only)

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react"

const BRAND = "#4EDCF6"
const DARK = "#141414"

const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "")
const fmtMobile = (m) => {
  const d = digitsOnly(m)
  if (d.length !== 10) return d
  return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`
}

const initialsFromLead = (lead) => {
  const fn = String(lead?.firstName || "").trim()
  const ln = String(lead?.lastName || "").trim()
  const a = fn ? fn[0] : ""
  const b = ln ? ln[0] : ""
  const s = (a + b).toUpperCase()
  return s || "MM"
}

const fullNameFromLead = (lead) => {
  const fn = String(lead?.firstName || "").trim()
  const ln = String(lead?.lastName || "").trim()
  const name = `${fn} ${ln}`.trim()
  return name || "Lead"
}

const getDialerBase = () => {
  return (
    (typeof process !== "undefined" && process?.env?.REACT_APP_DIALER_API_BASE) ||
    (typeof window !== "undefined" && window?.REACT_APP_DIALER_API_BASE) ||
    ""
  )
}

// Optional: simple shared auth between your app and your dialer API
const getDialerClientKey = () => {
  return (
    (typeof process !== "undefined" && process?.env?.REACT_APP_DIALER_CLIENT_KEY) ||
    (typeof window !== "undefined" && window?.REACT_APP_DIALER_CLIENT_KEY) ||
    ""
  )
}

const IconPhone = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M22 16.9v2.7a2.1 2.1 0 0 1-2.3 2 19.8 19.8 0 0 1-8.6-3.1 19.2 19.2 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.3 2.1 2.1 0 0 1 4.1 2h2.7a2 2 0 0 1 2 1.7c.1.8.3 1.6.6 2.4a2 2 0 0 1-.5 2.1L7.9 9.9a16 16 0 0 0 6.2 6.2l1.7-1a2 2 0 0 1 2.1-.5c.8.3 1.6.5 2.4.6a2 2 0 0 1 1.7 1.8Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconHang = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 15.5c1.6-1.6 4.1-2.5 9-2.5s7.4.9 9 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 15.5v4M18 15.5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const IconOpen = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14 3h7v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 14 21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M21 14v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconX = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const IconSpark = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2l1.2 4.5L18 8l-4.8 1.5L12 14l-1.2-4.5L6 8l4.8-1.5L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M5 14l.7 2.6L8 17.4l-2.3.8L5 21l-.7-2.6L2 17.4l2.3-.8L5 14Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
)

export default function DialerPanel({ lead, disabled = false, onScreenPop, mode = "default", className }) {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState("idle") // idle | dialling | ringing | in-call | ending | failed
  const [busy, setBusy] = useState(false)
  const [callId, setCallId] = useState("")
  const pollRef = useRef(null)

  const dialerBase = useMemo(() => String(getDialerBase() || "").trim(), [])
  const clientKey = useMemo(() => String(getDialerClientKey() || "").trim(), [])
  const hasApi = !!dialerBase

  const mobile = useMemo(() => digitsOnly(lead?.mobile || ""), [lead?.mobile])
  const canUse = !!mobile && mobile.length === 10 && !disabled

  const initials = useMemo(() => initialsFromLead(lead), [lead])
  const name = useMemo(() => fullNameFromLead(lead), [lead])
  const appType = String(lead?.applicationType || "").trim()

  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen((v) => !v), [])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, close])

  useEffect(() => {
    if (open && !canUse) setOpen(false)
  }, [open, canUse])

  const doScreenPop = useCallback(() => {
    if (!onScreenPop) return
    if (!mobile) return
    onScreenPop(mobile)
  }, [onScreenPop, mobile])

  const apiFetch = useCallback(
    async (path, opts = {}) => {
      if (!dialerBase) throw new Error("Dialer API base not set")
      const headers = {
        "Content-Type": "application/json",
        ...(opts.headers || {}),
      }
      if (clientKey) headers["x-mm-dialer-key"] = clientKey
      const res = await fetch(`${dialerBase}${path}`, { ...opts, headers })
      const text = await res.text()
      let json = null
      try {
        json = text ? JSON.parse(text) : null
      } catch {
        // ignore
      }
      if (!res.ok) {
        const msg = json?.message || json?.error || text || `HTTP ${res.status}`
        throw new Error(msg)
      }
      return json
    },
    [dialerBase, clientKey]
  )

  const startPolling = useCallback(
    (nextCallId) => {
      const id = nextCallId || callId
      if (!id) return
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(async () => {
        try {
          const r = await apiFetch(`/dialer/status?callId=${encodeURIComponent(id)}`, { method: "GET" })
          const s = String(r?.status || "").toLowerCase()
          if (s) setStatus(s)
          // stop polling if terminal
          if (["idle", "failed", "ended"].includes(s)) {
            clearInterval(pollRef.current)
            pollRef.current = null
            setCallId("")
          }
        } catch {
          // don’t hard-fail UI if poll hiccups
        }
      }, 1200)
    },
    [apiFetch, callId]
  )

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const handleCall = async () => {
    if (!canUse || !hasApi) return
    setBusy(true)
    try {
      setStatus("dialling")
      const payload = {
        to: mobile,
        lead: {
          firstName: lead?.firstName || "",
          lastName: lead?.lastName || "",
          email: lead?.email || "",
          contactId: lead?.contactId || "",
          opportunityId: lead?.opportunityId || "",
          applicationType: lead?.applicationType || "",
          step: lead?.step || "",
        },
        // optional: helps backend decide AU formatting, agent, etc.
        country: "AU",
      }
      const r = await apiFetch(`/dialer/call`, { method: "POST", body: JSON.stringify(payload) })
      const newCallId = String(r?.callId || r?.id || "")
      if (newCallId) setCallId(newCallId)
      // if backend returns immediate status
      if (r?.status) setStatus(String(r.status).toLowerCase())
      // start status polling
      startPolling(newCallId)
    } catch {
      setStatus("failed")
    } finally {
      setBusy(false)
    }
  }

  const handleHang = async () => {
    if (!canUse || !hasApi) return
    if (!callId) {
      setStatus("idle")
      return
    }
    setBusy(true)
    try {
      setStatus("ending")
      await apiFetch(`/dialer/hangup`, { method: "POST", body: JSON.stringify({ callId }) })
      setStatus("idle")
      setCallId("")
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = null
    } catch {
      setStatus("in-call")
    } finally {
      setBusy(false)
    }
  }

  const statusLabel = useMemo(() => {
    if (status === "dialling") return "Dialling"
    if (status === "ringing") return "Ringing"
    if (status === "in-call") return "In call"
    if (status === "ending") return "Ending"
    if (status === "failed") return "Failed"
    return "Idle"
  }, [status])

  const statusTone = useMemo(() => {
    if (!canUse) return { fg: "rgba(20,20,20,0.55)", bg: "rgba(20,20,20,0.06)", dot: "rgba(20,20,20,0.35)" }
    if (status === "in-call") return { fg: "#065f46", bg: "rgba(34,197,94,0.14)", dot: "#22c55e" }
    if (status === "ringing" || status === "dialling") return { fg: "#92400e", bg: "rgba(245,158,11,0.16)", dot: "#f59e0b" }
    if (status === "failed") return { fg: "rgba(127,29,29,0.95)", bg: "rgba(239,68,68,0.10)", dot: "rgba(239,68,68,0.95)" }
    return { fg: "rgba(20,20,20,0.7)", bg: "rgba(20,20,20,0.06)", dot: "rgba(20,20,20,0.35)" }
  }, [status, canUse])

  const shellStyles = {
    overlay: { position: "fixed", inset: 0, zIndex: 9998, background: "rgba(10,10,10,0.32)", backdropFilter: "blur(6px)" },
    drawer: {
      position: "fixed",
      right: 16,
      bottom: 16,
      zIndex: 9999,
      width: "min(420px, calc(100vw - 32px))",
      borderRadius: 22,
      background: "rgba(255,255,255,0.92)",
      border: "1px solid rgba(20,20,20,0.10)",
      boxShadow: "0 24px 70px rgba(0,0,0,0.26)",
      overflow: "hidden",
      transform: open ? "translateY(0)" : "translateY(12px)",
      opacity: open ? 1 : 0,
      transition: "all 180ms ease",
    },
    header: {
      padding: "12px 14px",
      background:
        "linear-gradient(180deg, rgba(78,220,246,0.26) 0%, rgba(255,255,255,0.94) 60%, rgba(255,255,255,0.92) 100%)",
      borderBottom: "1px solid rgba(20,20,20,0.08)",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    title: { fontWeight: 900, fontSize: 16, letterSpacing: -0.2, color: DARK },
    sub: { fontSize: 12, fontWeight: 700, color: "rgba(20,20,20,0.55)" },
    statusPill: {
      marginLeft: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 999,
      background: statusTone.bg,
      color: statusTone.fg,
      border: "1px solid rgba(20,20,20,0.08)",
      fontWeight: 800,
      fontSize: 12,
    },
    dot: { width: 8, height: 8, borderRadius: 99, background: statusTone.dot },
    closeBtn: {
      marginLeft: 8,
      width: 36,
      height: 36,
      borderRadius: 12,
      border: "1px solid rgba(20,20,20,0.10)",
      background: "rgba(255,255,255,0.75)",
      display: "grid",
      placeItems: "center",
      cursor: "pointer",
    },
    body: { padding: 14 },
    leadCard: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 12px",
      borderRadius: 18,
      border: "1px solid rgba(20,20,20,0.10)",
      background: "rgba(255,255,255,0.78)",
    },
    avatar: {
      width: 44,
      height: 44,
      borderRadius: 16,
      display: "grid",
      placeItems: "center",
      background: `linear-gradient(135deg, rgba(78,220,246,0.95) 0%, rgba(78,220,246,0.45) 100%)`,
      color: DARK,
      fontWeight: 900,
      letterSpacing: 0.4,
      boxShadow: "0 10px 24px rgba(78,220,246,0.28)",
      border: "1px solid rgba(20,20,20,0.10)",
    },
    leadName: { fontSize: 15, fontWeight: 900, color: DARK, lineHeight: 1.1 },
    leadMeta: { marginTop: 4, fontSize: 12, fontWeight: 800, color: "rgba(20,20,20,0.62)" },
    tagRow: { marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" },
    tag: {
      padding: "6px 10px",
      borderRadius: 999,
      border: "1px solid rgba(20,20,20,0.10)",
      background: "rgba(20,20,20,0.04)",
      fontWeight: 800,
      fontSize: 12,
      color: "rgba(20,20,20,0.70)",
      whiteSpace: "nowrap",
    },
    setupNotice: {
      marginTop: 10,
      padding: "10px 12px",
      borderRadius: 14,
      border: "1px solid rgba(20,20,20,0.10)",
      background: "rgba(20,20,20,0.03)",
      display: "flex",
      alignItems: "center",
      gap: 10,
      color: "rgba(20,20,20,0.70)",
      fontSize: 12,
      fontWeight: 750,
    },
    actions: { marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 },
    actionBtn: (variant) => {
      const base = {
        height: 44,
        borderRadius: 16,
        border: "1px solid rgba(20,20,20,0.10)",
        background: "rgba(255,255,255,0.82)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        fontWeight: 900,
        cursor: canUse && !busy ? "pointer" : "not-allowed",
        opacity: canUse ? 1 : 0.5,
        transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
        userSelect: "none",
      }
      if (variant === "primary") {
        return {
          ...base,
          background: `linear-gradient(180deg, ${BRAND} 0%, rgba(78,220,246,0.85) 100%)`,
          color: DARK,
          boxShadow: "0 12px 26px rgba(78,220,246,0.28)",
        }
      }
      if (variant === "danger") {
        return { ...base, background: "rgba(239,68,68,0.08)", color: "rgba(127,29,29,0.95)" }
      }
      return base
    },
    tip: {
      marginTop: 12,
      paddingTop: 12,
      borderTop: "1px solid rgba(20,20,20,0.08)",
      fontSize: 12,
      fontWeight: 750,
      color: "rgba(20,20,20,0.65)",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    fab: {
      position: "fixed",
      right: 18,
      bottom: 18,
      zIndex: 9997,
      width: 56,
      height: 56,
      borderRadius: 999,
      border: "1px solid rgba(20,20,20,0.10)",
      background: `linear-gradient(180deg, ${BRAND} 0%, rgba(78,220,246,0.84) 100%)`,
      boxShadow: "0 18px 44px rgba(0,0,0,0.22)",
      color: DARK,
      display: "grid",
      placeItems: "center",
      cursor: canUse ? "pointer" : "not-allowed",
      opacity: canUse ? 1 : 0.55,
      transform: open ? "scale(0.98)" : "scale(1)",
      transition: "transform 160ms ease, opacity 160ms ease",
    },
    fabBadge: {
      position: "absolute",
      top: 7,
      right: 7,
      width: 10,
      height: 10,
      borderRadius: 99,
      background: statusTone.dot,
      boxShadow: "0 0 0 3px rgba(255,255,255,0.65)",
    },
  }

  const Inner = (
    <div className={className}>
      <div style={shellStyles.header}>
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.05 }}>
          <div style={shellStyles.title}>Dialler</div>
          <div style={shellStyles.sub}>CallHippo integration</div>
        </div>

        <div style={shellStyles.statusPill} title={`Status: ${statusLabel}${callId ? ` • ${callId}` : ""}`}>
          <span style={shellStyles.dot} />
          {statusLabel}
        </div>

        {mode !== "drawerInner" ? (
          <button type="button" onClick={close} style={shellStyles.closeBtn} aria-label="Close dialler">
            <IconX />
          </button>
        ) : null}
      </div>

      <div style={shellStyles.body}>
        <div style={shellStyles.leadCard}>
          <div style={shellStyles.avatar} title={name}>
            {initials}
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={shellStyles.leadName}>{name}</div>
            <div style={shellStyles.leadMeta}>{fmtMobile(mobile)}</div>
          </div>

          <div style={shellStyles.tagRow}>
            {appType ? <div style={shellStyles.tag}>{appType.toUpperCase()}</div> : null}
            <button
              type="button"
              onClick={doScreenPop}
              disabled={!canUse}
              style={{
                ...shellStyles.tag,
                background: "rgba(255,255,255,0.75)",
                cursor: canUse ? "pointer" : "not-allowed",
              }}
              title="Screen-pop (open this file by mobile)"
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <IconOpen size={16} /> Open
              </span>
            </button>
          </div>
        </div>

        {!hasApi ? (
          <div style={shellStyles.setupNotice}>
            <IconSpark />
            <div style={{ minWidth: 0 }}>
              Dialler not configured. Set <span style={{ fontFamily: "monospace" }}>REACT_APP_DIALER_API_BASE</span>.
            </div>
          </div>
        ) : null}

        <div style={shellStyles.actions}>
          <button
            type="button"
            onClick={handleCall}
            disabled={!canUse || busy || !hasApi}
            style={shellStyles.actionBtn("primary")}
            title="Call"
            onMouseDown={(e) => e.currentTarget && (e.currentTarget.style.transform = "translateY(1px)")}
            onMouseUp={(e) => e.currentTarget && (e.currentTarget.style.transform = "translateY(0px)")}
          >
            <IconPhone />
            Call
          </button>

          <button
            type="button"
            onClick={handleHang}
            disabled={!canUse || busy || !hasApi || !callId || status !== "in-call"}
            style={shellStyles.actionBtn("danger")}
            title="Hang up"
          >
            <IconHang />
            Hang
          </button>

          <button type="button" onClick={doScreenPop} disabled={!canUse} style={shellStyles.actionBtn("default")} title="Open lead">
            <IconOpen />
            Open
          </button>
        </div>

        <div style={shellStyles.tip}>
          <IconSpark />
          Tip: Log outcomes in <strong>Broker Notes</strong> (source of truth in Mercury).
        </div>
      </div>
    </div>
  )

  if (mode === "drawerInner") return Inner

  return (
    <>
      <button
        type="button"
        style={shellStyles.fab}
        onClick={() => {
          if (!canUse) return
          setOpen(true)
        }}
        aria-label={canUse ? "Open dialler" : "Enter a valid mobile first"}
        title={canUse ? "Dialler" : "Enter a valid mobile first"}
      >
        <IconPhone size={20} />
        <span style={shellStyles.fabBadge} />
      </button>

      {open ? (
        <>
          <div style={shellStyles.overlay} onClick={close} />
          <div style={shellStyles.drawer}>{Inner}</div>
        </>
      ) : null}
    </>
  )
}